import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import FilterHeader from './FilterHeader';
import FilterCondition from './FilterCondition';
import FilterLogicOperator from './FilterLogicOperator';
import FilterActions from './FilterActions';
import './styles/FilterModal.scss';

export interface FilterConditionType {
  operator: string;
  value: string;
}

interface FilterModalProps {
  columnId: string;
  setFilter: any;
  preFilteredRows: any;
  filterValue: any;
  clearFilterForColumn: (columnId: string) => void;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>; // New prop to get the reference of the clicked column
  appliedFilters?: any; // Optional prop to pass applied filters
  children?: React.ReactNode;
  isFullscreen?: boolean;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  columnId,
  setFilter,
  preFilteredRows,
  filterValue,
  clearFilterForColumn,
  onClose,
  appliedFilters,
  triggerRef,
  isFullscreen = false,
}) => {
  const isDate = columnId.toLowerCase().includes('date') || columnId.toLowerCase().includes('created');

  const [conditions, setConditions] = useState<FilterConditionType[]>([
    { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
    { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
  ]);

  const [logicOperator, setLogicOperator] = useState<string>('And');
  const modalRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const hasCalculatedInitialPosition = useRef(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const modalElement = modalRef.current;
      if (modalElement && !modalElement.contains(event.target as Node)) {
        event.preventDefault(); // Prevent default behavior
        onClose(); // Close the modal when clicking outside
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [onClose]);

  // Optimized position calculation function
  const calculateOptimalPosition = React.useCallback(() => {
    if (!triggerRef?.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const fullscreenContainer = isFullscreen
      ? (document.querySelector('.card-table-wrapper.fullscreen') as HTMLElement)
      : null;

    const containerRect = fullscreenContainer
      ? fullscreenContainer.getBoundingClientRect()
      : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };

    const scrollX = fullscreenContainer ? 0 : window.scrollX;
    const scrollY = fullscreenContainer ? 0 : window.scrollY;

    const modalElement = modalRef.current;
    const modalWidth = modalElement?.offsetWidth || 300;
    const modalHeight = modalElement?.offsetHeight || 400;

    let left = triggerRect.left - containerRect.left + scrollX;
    let top = triggerRect.bottom - containerRect.top + scrollY + 4;

    if (left + modalWidth > containerRect.width) {
      left = Math.max(12, triggerRect.right - containerRect.left + scrollX - modalWidth);
    } else {
      left = Math.max(12, left);
    }

    const spaceBelow = containerRect.height - (triggerRect.bottom - containerRect.top);
    const spaceAbove = triggerRect.top - containerRect.top;

    if (spaceBelow < modalHeight && spaceAbove > spaceBelow) {
      top = triggerRect.top - containerRect.top + scrollY - modalHeight - 4;
    } else if (spaceBelow < modalHeight) {
      top = Math.max(scrollY + 12, containerRect.height + scrollY - modalHeight - 12);
    }
    top = Math.max(scrollY + 12, top);

    const newPosition = { top, left };

    if (!position || Math.abs(position.top - top) > 5 || Math.abs(position.left - left) > 5) {
      setPosition(newPosition);
      hasCalculatedInitialPosition.current = true;
    }
  }, [triggerRef, position, isFullscreen]);

  useEffect(() => {
    calculateOptimalPosition();

    const handleResize = () => calculateOptimalPosition();
    const handleScroll = () => calculateOptimalPosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [calculateOptimalPosition]);

  useEffect(() => {
    if (position && modalRef.current && !hasCalculatedInitialPosition.current) {
      calculateOptimalPosition();
    }
  }, [position, calculateOptimalPosition]);

  useEffect(() => {
    const filter = appliedFilters?.find((filter: any) => filter.columnId === columnId);
    if (filter) {
      const { conditions = [], logicOperator } = filter.filterValue;
      const padded = [
        conditions[0] ?? { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
        conditions[1] ?? { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
      ];
      setConditions(padded);
      setLogicOperator(logicOperator || 'And');
    } else {
      setConditions([
        { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
        { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
      ]);
      setLogicOperator('And');
    }
  }, [appliedFilters, columnId]);

  const handleOperatorChange = (index: number, value: string) => {
    const newConditions = [...conditions];
    newConditions[index].operator = value;
    setConditions(newConditions);
  };

  const handleValueChange = (index: number, value: string) => {
    const newConditions = [...conditions];
    newConditions[index].value = value;
    setConditions(newConditions);
  };

  const handleLogicOperatorChange = (value: string) => {
    setLogicOperator(value);
  };

  const handleClear = () => {
    setConditions([
      { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
      { operator: isDate ? 'Is equal to' : 'Contains', value: '' },
    ]);
    setLogicOperator('And');
    clearFilterForColumn(columnId);
    onClose();
  };

  const handleApply = () => {
    console.log('Applied filters:', { conditions, logicOperator });
    // Implement your filter application logic here
    console.log('Pre-filtered rows:', preFilteredRows);
    console.log('Current filter value:', filterValue);
    // Example: setFilter function to apply the filter
    // This is where you would typically apply the filter logic to your data
    // For demonstration, we just log the conditions and logic operator
    console.log('Conditions:', conditions);
    console.log('Logic Operator:', logicOperator);

    const activeConditions = conditions.filter((condition) => condition.value !== undefined && condition.value !== '');

    setFilter({ conditions: activeConditions, logicOperator });
    onClose();
  };

  return createPortal(
    <div
      className='filter-modal-overlay'
      ref={modalRef}
      style={{
        position: 'absolute',
        top: position?.top || 0,
        left: position?.left || 0,
        zIndex: isFullscreen ? 10000 : 800,
        visibility: position ? 'visible' : 'hidden', // Hide until position is calculated
      }}
    >
      <main className='filter-menu'>
        <section className='filter-menu__container'>
          <div className='filter-menu__content'>
            <FilterHeader />
            <div className='filter-menu__conditions' onClick={(e) => e.stopPropagation()}>
              <FilterCondition
                operator={conditions[0]?.operator ?? (isDate ? 'Is equal to' : 'Contains')}
                value={conditions[0]?.value ?? ''}
                onOperatorChange={(value) => handleOperatorChange(0, value)}
                onValueChange={(value) => handleValueChange(0, value)}
                type={isDate ? 'date' : 'text'}
              />
              <FilterLogicOperator value={logicOperator} onChange={handleLogicOperatorChange} />
              <FilterCondition
                operator={conditions[1]?.operator ?? (isDate ? 'Is equal to' : 'Contains')}
                value={conditions[1]?.value ?? ''}
                onOperatorChange={(value) => handleOperatorChange(1, value)}
                onValueChange={(value) => handleValueChange(1, value)}
                type={isDate ? 'date' : 'text'}
              />
            </div>
          </div>
          <FilterActions onClear={handleClear} onApply={handleApply} />
        </section>
      </main>
    </div>,
    isFullscreen
      ? document.querySelector('.card-table-wrapper.fullscreen') || document.body
      : document.querySelector('.outlet-container') || document.body, // portal mounts to body
  );
};
